<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay eh-popup-overlay" data-test="remove-file-dialog" @click.self="onCancel">
      <div class="modal-content delete-modal eh-popup-panel" role="dialog" aria-modal="true" aria-labelledby="remove-file-dialog-title">
        <div class="modal-header danger">
          <h3 id="remove-file-dialog-title">
            <span class="glyphicon glyphicon-warning-sign"></span>
            Remove document
          </h3>
          <button type="button" class="close-btn" aria-label="Close" :disabled="busy" @click="onCancel">
            <span class="glyphicon glyphicon-remove bi-x-circle"></span>
          </button>
        </div>
        <div class="modal-body">
          <p>
            Remove <strong>{{ fileName }}</strong>? It stops being available to everyone who could open it.
            It is kept, not erased, for record retention.
          </p>
          <p v-if="note" class="remove-note" data-test="remove-file-note">{{ note }}</p>
          <label class="remove-reason-label" for="remove-file-reason">Why are you removing it?</label>
          <!-- touched is set from the person's typing, not a watcher on reason: a
               watcher would also fire for the reset when the dialog reopens and
               show the required-reason error before anything was typed. -->
          <textarea
            id="remove-file-reason"
            v-model="reason"
            class="remove-reason-input"
            rows="3"
            :maxlength="maxLength"
            :disabled="busy"
            data-test="remove-file-reason"
            @input="touched = true"
          ></textarea>
          <p v-if="shownError" class="remove-error" role="alert" data-test="remove-file-error">{{ shownError }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" :disabled="busy" @click="onCancel">Cancel</button>
          <button
            type="button"
            class="btn-danger"
            :disabled="!canConfirm"
            data-test="remove-file-confirm"
            @click="onConfirm"
          >
            <span class="glyphicon glyphicon-trash bi-trash"></span>
            Remove
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { DELETE_REASON_MAX, validateDeleteReason } from "@/utils/fileDelete";

/**
 * The confirmation for removing a stored file (a directive document or a
 * message attachment). A reason is required: keyon keeps the file for record
 * retention and stores why it was removed. All rules live in
 * @/utils/fileDelete; this only collects the reason and emits it.
 */
export default {
  name: "RemoveFileDialog",
  props: {
    show: { type: Boolean, default: false },
    fileName: { type: String, default: "" },
    busy: { type: Boolean, default: false },
    errorText: { type: String, default: "" },
    // What else the removal does, when it does more than remove the file.
    note: { type: String, default: "" },
  },
  emits: ["confirm", "cancel"],
  data() {
    return {
      reason: "",
      touched: false,
    };
  },
  computed: {
    maxLength() {
      return DELETE_REASON_MAX;
    },
    checked() {
      return validateDeleteReason(this.reason);
    },
    canConfirm() {
      return this.checked.ok && !this.busy;
    },
    shownError() {
      if (this.errorText) return this.errorText;
      return this.touched && !this.checked.ok ? this.checked.error : "";
    },
  },
  watch: {
    show(open) {
      if (open) {
        this.reason = "";
        this.touched = false;
      }
    },
  },
  methods: {
    onCancel() {
      if (this.busy) return;
      this.$emit("cancel");
    },
    onConfirm() {
      this.touched = true;
      if (!this.canConfirm) return;
      this.$emit("confirm", this.checked.reason);
    },
  },
};
</script>

<style scoped>
/* The same confirmation look as the credential delete (PdfsProviderDocuments). */
.modal-overlay {
  /* Backdrop color + z-index from brand-ds (.eh-popup-overlay / --eh-popup-*). */
  position: fixed;
  inset: 0;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-content {
  /* Surface, border, radius from brand-ds (.eh-popup-panel / --eh-popup-*). */
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
}

.modal-header.danger h3 {
  color: #c53030;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.remove-note {
  margin: 8px 0 0;
  font-weight: 600;
}

.remove-reason-label {
  display: block;
  margin: 12px 0 6px;
  font-weight: 600;
  font-size: 14px;
}

.remove-reason-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
}

.remove-error {
  margin: 8px 0 0;
  color: #c53030;
  font-size: 13px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
}

.btn-secondary,
.btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-secondary {
  background-color: #676464;
  border: 1px solid #e2e8f0;
  color: #ffffff;
}

.btn-danger {
  border: none;
  color: white;
  background: #dd5555;
}

.btn-danger:hover:not(:disabled) {
  background: #c53030;
}

.btn-danger:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .modal-footer {
    flex-wrap: wrap;
  }
}
</style>
